import type { MinCopy } from '@/i18n/types'

export const minEn: MinCopy = {
  brand: {
    name: 'Lexio',
    tagline: 'Understand English as you read',
    beta: 'Beta',
  },
  nav: {
    today: 'Today',
    catalog: 'Browse catalog',
    words: 'Your vocabulary',
    reading: 'Reading',
    refresh: 'Refresh your memory',
    challenge: 'Challenge yourself',
    progress: 'Progress',
    practice: 'Practice',
    settings: 'Settings',
    feedback: 'Feedback',
    faq: 'FAQ',
    mobileWords: 'Vocab',
    mobileReview: 'Review',
    more: 'More',
    logout: 'Log out',
    mobileNavAria: 'Mobile navigation',
    closeMenuAria: 'Close menu',
    close: 'Close',
  },
  auth: {
    localeSwitcherLabel: 'Interface language',
    loginTitle: 'Lexio',
    loginDescription: 'Sign in to continue your language learning journey.',
    email: 'Email',
    password: 'Password',
    forgotPassword: 'Forgot password?',
    signIn: 'Sign in',
    or: 'or',
    continueWithGoogle: 'Continue with Google',
    noAccount: "Don't have an account?",
    registerLink: 'Register',
    invalidEmail: 'Invalid email address',
    passwordRequired: 'Password is required',
    googleAuthFailed: 'Could not sign in with Google. Try email and password or try again later.',
    unableSignIn: 'Unable to sign in right now. Please try again.',
    invalidCredentials: 'Invalid email or password',
    tooManyAttempts: 'Too many attempts. Please try again later.',
    registerTitle: 'Create your Lexio account',
    registerDescription: 'Build vocabulary, reading, and review habits in one place.',
    displayName: 'Display name',
    confirmPassword: 'Confirm password',
    createAccount: 'Create account',
    agreePrefix: 'By creating an account, you agree to our',
    termsOfService: 'Terms of Service',
    agreeAnd: 'and',
    privacyPolicy: 'Privacy Policy',
    agreeSuffix: '.',
    haveAccount: 'Already have an account?',
    signInLink: 'Sign in',
    passwordMin: 'Password must be at least 8 characters',
    passwordUpper: 'Must contain at least 1 uppercase letter',
    passwordDigit: 'Must contain at least 1 digit',
    passwordsMismatch: "Passwords don't match",
    unableRegister: 'Unable to create your account right now. Please try again.',
    emailAlreadyRegistered: 'This email is already registered',
    resetTitle: 'Reset your password',
    resetDescription: 'Enter your email address and we will send you a reset link.',
    sendResetLink: 'Send reset link',
    backTo: 'Back to',
    resetSent:
      "If an account exists with this email, you'll receive a reset link shortly. Please also check your Spam or Junk folder.",
    resetTooMany: 'Too many reset requests from this device. Please wait a few minutes and try again.',
    resetUnable: 'Unable to send a reset link right now. Please try again later.',
    chooseNewPasswordTitle: 'Choose a new password',
    chooseNewPasswordDescription: 'Enter a strong new password to regain access to your account.',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    saveNewPassword: 'Save new password',
    rememberedPassword: 'Remembered your password?',
    resetLinkInvalid: 'This reset link is invalid or has expired.',
    resetUnableConfirm: 'Unable to reset your password right now. Please try again later.',
    passwordResetSuccess: 'Password reset successful. Please sign in.',
    checkEmailTitle: 'Check your email',
    verifyingTitle: 'Verifying your email',
    verifyingMessage: 'Verifying your email…',
    verificationSent:
      'We sent a verification link to your email address. Please also check your Spam or Junk folder.',
    emailPlaceholder: 'you@example.com',
    sendVerificationLink: 'Send a new verification link',
    alreadyVerified: 'Already verified?',
    verifyingHint: 'Keep this page open while we activate your account.',
    verificationLinkInvalid: 'This verification link is invalid or has expired. Request a new link below.',
    enterRegisteredEmail: 'Enter the email address you used to register.',
    verificationResent:
      'If this account still needs verification, a new link has been sent. Please also check your Spam or Junk folder.',
    verificationTooMany: 'Too many requests. Please try again later.',
    verificationUnable: 'Unable to send a verification link right now. Please try again.',
    signingInWithGoogle: 'Signing you in with Google...',
  },
  onboarding: {
    wizardTitle: 'Welcome to Lexio',
    wizardSubtitle: 'Tell us a bit about yourself so we can personalize your learning.',
    stepUiTitle: 'What language should the app use?',
    stepUiDescription: 'Menus, buttons, and the intro will use this language. You can change it later in Settings.',
    uiLanguageLabel: 'Interface language',
    stepNativeTitle: 'What is your learning language?',
    stepNativeDescription: 'We use it for translations, word popups, and AI explanations.',
    stepThemeTitle: 'How should Lexio look?',
    stepThemeDescription: 'Pick light or dark — you can change this anytime in Settings.',
    stepLevelTitle: 'What is your English level?',
    stepLevelDescription: 'Articles, reviews, and practice will match this level.',
    stepDurationTitle: 'How long do you want to study each day?',
    stepDurationDescription: 'Your daily guided session will fit this time.',
    stepDurationLabel: '{minutes} minutes',
    stepTopicsTitle: 'What do you like to read about?',
    stepTopicsDescription: 'Pick a few topics — we use them to recommend articles. You can skip this step.',
    stepTopicsSkip: 'Skip for now',
    stepCompleteTitle: "You're all set!",
    stepCompleteDescription: 'Lexio will tailor reading, vocabulary, and daily sessions to your choices.',
    stepCompleteHint: 'You can change any of these anytime in Settings.',
    next: 'Continue',
    back: 'Back',
    finish: 'Start learning',
    languageLabel: 'Learning language',
    levelLabel: 'Level',
    saveFailed: 'Could not save your preferences. Please try again.',
  },
  learningDemo: {
    skip: 'Skip intro',
    introTitle: 'One day of learning',
    introPhilosophy:
      "You don't need to memorize thousands of words. Just read, save the words you care about, and Lexio will help you remember them at the right time.",
    introCta: 'Show me how',
    questStep: 'Step {current} of {total}',
    readTitle: 'Read',
    readBody: 'Read the short passage. When you meet an unfamiliar word, tap it.',
    understandTitle: 'Understand',
    understandBody: 'We explain the word in context. If it helps you, save it to your vocabulary.',
    vocabularyTitle: 'Your vocabulary',
    vocabularyBody: 'Your saved words live here — this is your personal list to learn.',
    refreshTitle: 'Refresh',
    refreshBody: 'Later today or tomorrow, come back to review. Spaced repetition helps words stick.',
    todayTitle: 'Today',
    todayBody: 'Your home base for the day — we prepare what to read and review based on your progress.',
    tapWordHint: 'Tap the highlighted word',
    saveWord: 'Save word',
    saving: 'Saving…',
    saved: 'Saved!',
    savedWordLabel: 'Your first word',
    vocabularyStatusNew: 'New',
    continue: 'Continue',
    completeTitle: "You're ready",
    completeBody: "You've already started your vocabulary. Open your first article and tap any word you don't know.",
    completeCta: 'Start reading',
    replay: 'Replay intro',
    replayDescription: 'Walk through the interactive intro again.',
    readLabel: 'Read',
    understandLabel: 'Understand',
    means: 'Means',
    inContext: 'In this sentence',
    definitionLabel: 'Definition',
    exampleLabel: 'Example',
    vocabularyMoreHint:
      'Your vocabulary also tracks progress, review history, and personal notes — more than a simple word list.',
    refreshFlipHint: 'Tap the card to flip',
    refreshCardLabel: 'Review card',
    refreshMoreHint:
      'Refresh also has several review modes, session stats, and spaced-repetition scheduling — this is just a quick peek.',
    todayPreviewHint: 'A typical day looks like this:',
    todayCardLabel: 'Your day',
    todayStepReading: 'Continue reading',
    todayStepReadingHint: 'Pick up an article at your level',
    todayStepNewWords: 'Save new words',
    todayStepNewWordsHint: 'Tap words while reading to grow your list',
    todayStepRefresh: 'Refresh memory',
    todayStepRefreshHint: 'Review due words so they stick',
    todayMoreHint: 'Today brings reading, vocabulary, and review together in one place.',
    completeHelpHint:
      'Questions? Open FAQ from the sidebar (or More on mobile). To share an idea or report a bug, tap Feedback — it sits right above FAQ.',
    translation: 'Translation',
    playPronunciation: 'Play pronunciation',
    demoArticleTitle: 'Before School',
    saveWordFailed: 'Could not save the word. Please try again.',
    loadExplanationFailed: 'Could not load the word explanation. Please try again.',
    loadingExplanation: 'Loading explanation…',
  },
  faq: {
    title: 'Frequently asked questions',
    intro: 'Short answers about how Lexio works today — reading, saving words, reviews, and your account.',
    feedbackTitle: 'Tell us what you think',
    feedbackHint: 'Likes, friction, bugs, ideas — we read every note.',
    breadcrumb: 'FAQ',
    items: [
      {
        id: 'what-is-lexio',
        question: 'What is Lexio?',
        answer:
          'Lexio helps you understand English while you read. Tap a word or phrase for a clear explanation in context, save what matters, and meet it again later through spaced review — so you keep reading instead of opening Google.',
      },
      {
        id: 'how-to-start',
        question: 'How do I start?',
        answer:
          'Create a free account, verify your email, pick your native language and level, then try the short learning demo. After that, open Reading, tap words you do not know, save them, and do Reviews when they are due.',
      },
      {
        id: 'tap-understand',
        question: 'How does tap-to-understand work?',
        answer:
          'In an article, tap a word (or select a short phrase). Lexio shows the meaning that fits this sentence, related forms when useful, and other meanings when the word has more than one sense. Choose the meaning you want to learn before you save.',
      },
      {
        id: 'save-meaning',
        question: 'What happens when I save a word?',
        answer:
          'The word goes into your vocabulary as one card. If you add another meaning later, it expands the same card — you do not get duplicate rows for the same word. Reviews use that card so you practice what you actually saved.',
      },
      {
        id: 'reviews',
        question: 'How do reviews work?',
        answer:
          'Lexio uses spaced repetition (SM-2). Words you struggle with come back sooner; easier ones wait longer. Open Review from Today or the menu when you have cards due — that is the habit that makes reading stick.',
      },
      {
        id: 'email-verify',
        question: 'Why do I need to verify my email?',
        answer:
          'Email verification protects accounts from fake or stolen signups. After you register with email and password, open the link we send (check Spam if needed). Google sign-in is already verified and skips this step.',
      },
      {
        id: 'forgot-password',
        question: 'I forgot my password. What do I do?',
        answer:
          'On the login page choose Forgot password, enter your email, and open the reset link we send. The link expires in one hour. If nothing arrives, check Spam and make sure you used the same email as your account.',
      },
      {
        id: 'wrong-translation',
        question: 'What if a translation looks wrong?',
        answer:
          'First check Other meanings in the popup — the context sense is marked In this sentence. If it is still wrong, send feedback from Settings → Feedback or the form on this page. Wrong lookups are how we improve Trust Ops.',
      },
      {
        id: 'mobile',
        question: 'Does Lexio work on my phone?',
        answer:
          'Yes — Lexio is a mobile web app. Add it to your home screen if you like. Use the bottom navigation for Today, Reading, Vocabulary, and Review. Content scrolls above the menu so actions stay reachable.',
      },
      {
        id: 'free',
        question: 'Is Lexio free?',
        answer:
          'Yes for the core loop: reading, tap-to-understand, vocabulary, and reviews. Some AI-heavy extras may use daily limits. Paid plans may come later; free remains the way to understand English every day.',
      },
      {
        id: 'languages',
        question: 'Which languages are supported?',
        answer:
          'You read English. Teaching translations are in your learning language (Russian, Ukrainian, or Polish). The interface can also be English, Russian, Ukrainian, or Polish — pick it on the landing page or keep the language you chose when you signed up.',
      },
    ],
  },
  feedback: {
    like: 'I like this',
    dislike: 'This is confusing',
    bug: 'Something broke',
    idea: 'Idea',
    placeholder: 'What should we know?',
    emailPlaceholder: 'Your email',
    submit: 'Send feedback',
    done: 'Thanks — we got your feedback.',
    sendAnother: 'Send another',
    error: 'Could not send feedback. Try again in a moment.',
    settingsTitle: 'Feedback',
    settingsHint: 'Tell us what helps, what confuses you, or what broke.',
  },
  settings: {
    appearanceTitle: 'Appearance',
    appearanceDescription: 'Theme and interface language.',
    uiLanguageLabel: 'Interface language',
    uiLanguageHint: 'Menus, buttons, and navigation. Separate from your learning language.',
    themeLabel: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    learningLanguageLabel: 'Learning language',
    learningLanguageHint: 'Used for translations and AI explanations in your language.',
  },
}
